import { useEffect, useRef } from 'react'
import { useMe } from '@/api/auth'
import { useGo } from '@/lib/routing'
import { openCreateTask, toggleHelp, toggleSidebar, ui } from '@/lib/store'
import { isEditableTarget } from '@/lib/utils'
import { NAV, EXTRA_ROUTES, canSee } from './nav'

/**
 * Global keyboard shortcuts:
 *  ⌘K / Ctrl+K — command palette, "/" — search, "?" or F1 — help panel, "[" — collapse sidebar,
 *  "c" — new task, "g" then a letter — go to a section.
 */
export function useGlobalShortcuts() {
  const me = useMe().data
  const go = useGo()
  const gPending = useRef<number>(0)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        ui.set({ paletteOpen: !ui.get().paletteOpen })
        return
      }
      // F1 works everywhere (also in inputs): it has no text meaning.
      if (e.key === 'F1' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault()
        toggleHelp()
        return
      }
      if (e.metaKey || e.ctrlKey || e.altKey || isEditableTarget(e.target)) return
      const s = ui.get()
      // The docked help panel and the tour card are dialogs too, but must not block shortcuts.
      if (s.paletteOpen || s.createTaskOpen || document.querySelector('[role="dialog"]:not([data-help-panel]):not([data-tour-card]),[role="alertdialog"]')) return
      if (Date.now() - gPending.current < 1200) {
        gPending.current = 0
        const item = [...NAV.flatMap((x) => x.items), ...EXTRA_ROUTES].find((i) => i.key === e.key.toLowerCase() && canSee(i, me))
        if (item) {
          e.preventDefault()
          go(item.to)
        }
        return
      }
      switch (e.key) {
        case 'g':
          gPending.current = Date.now()
          break
        case '/':
          e.preventDefault()
          ui.set({ paletteOpen: true })
          break
        case '?':
          e.preventDefault()
          toggleHelp()
          break
        case '[':
          toggleSidebar()
          break
        case 'c':
          if (me?.permissions.includes('tasks.manage')) {
            e.preventDefault()
            openCreateTask()
          }
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [me, go])
}
